<template>
  <div class="h-full overflow-auto bg-neutral-100">
    <div class="">
      <!-- content -->
      <div class="mx-auto mb-5 mt-5 max-w-5xl px-2" v-if="!handlerNavItem.isAuthPending">
        <div
          class="mx-0 mb-4 mt-4 bg-sky-50 shadow sm:mx-5 sm:my-5 sm:rounded-lg md:mx-8 md:my-8 xl:mx-auto xl:max-w-5xl"
        >
          <!-- Card Content goes here -->
          <div class="justify-between px-4 py-5 sm:p-10 lg:flex">
            <div>
              <h1 class="text-2xl font-extrabold">
                {{ appSettingsStore.applicationTitle }}
                <span class="font-semibold">({{ appSettingsStore.pageTitleBase }})</span>
              </h1>
              <h2 class="mt-2 text-xl text-neutral-500 md:mt-1">
                <span class="font-semibold">Welcome, {{ userRef.firstName }}</span>
              </h2>
              <div class="prose prose-neutral mt-4 prose-li:marker:text-neutral-400 md:mt-5">
                <p>
                  <router-link
                    :to="{ name: handlerNavItem.name }"
                    v-if="handlerNavItem.isAuthorized"
                    class="text-sky-500"
                    ><span>View hazardous waste handler information</span></router-link
                  ><span v-else>View hazardous waste handler information</span>
                  managed in the Resource Conservation and Recovery Act Information (RCRAInfo) system. Handlers include
                  all generators, transporters, treaters, storers, and disposers of hazardous waste.
                </p>
              </div>
            </div>
            <div class="mt-6 items-start justify-start gap-5 space-y-5 sm:flex sm:space-y-0 lg:mt-0">
              <LogoutButton :as="BaseButton" variant="priority-hollow" class="w-full sm:w-auto" label="Sign Out" />
            </div>
          </div>
          <div class="flex justify-center" v-if="false">
            <h2 class="mt-2 mb-4 text-xl text-sky-500 md:mt-1 md:mb-6">
              View all
              <span class="font-semibold">
                <router-link :to="{ name: handlerNavItem.name }" v-if="handlerNavItem.isAuthorized">
                  <span class="cursor-pointer font-semibold hover:underline">{{ handlerNavItem.label }}</span>
                </router-link></span
              >
            </h2>
          </div>
        </div>
        <DashboardDataButtonGroup title="Active Handlers by Generator Status" class="pb-7"
          ><DashboardDataButton
            v-for="[id, item] in generatorsWithCounts"
            :key="id"
            :link-to="{
              name: routeNames.handlerList,
              query: { federalWasteGeneratorMurnIn: item.generatorMurn, statusMurn: LE.HandlerStatus.Active },
            }"
            :description="item.description"
            :data-value="item.count"
        /></DashboardDataButtonGroup>

        <DashboardDataButtonGroup title="Active Handlers by County"
          ><DashboardDataButton
            v-for="[id, item] in countyCodesWithCounts"
            :key="id"
            :link-to="{
              name: routeNames.handlerList,
              query: { countyCodeMurnIn: item.countyCodeMurn, statusMurn: LE.HandlerStatus.Active },
            }"
            :description="`${item.description} County`"
            :data-value="item.count"
        /></DashboardDataButtonGroup>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { BaseButton } from "@modernary/mui/components/button";
import { useAppNav, useAppSettingsStore } from "@modernary/mui/components/common";
import { LogoutButton } from "@modernary/mui/utils/authentication/buttons";
import { useUser } from "@modernary/mui/utils/authentication";

import { useAuthorization } from "@modernary/mui/utils/authorization";
import { getHandlers } from "@/views/handler/handlerApi";
import { routeNames } from "@/routes";

import { ref, computed, defineComponent } from "vue";
import DashboardDataButtonGroup from "@/components/DashboardDataButtonGroup.vue";
import DashboardDataButton from "@/components/DashboardDataButton.vue";
import { getLookupsFromLookupGroup, type LookupFilter } from "@modernary/mui/components/lookup";
import { LE } from "@/constants/enums";

export default defineComponent({
  name: "HomeInternal",
  setup() {
    const { userRef } = useUser();
    const { authorizationClient, AuthRequest, handlePreempts } = useAuthorization();
    const appSettingsStore = useAppSettingsStore();

    const { navigationItems, userNavigationItems } = useAppNav();

    let countyCodesWithCounts = ref(
      new Map<string, { id: string; description: string; count: number; countyCodeMurn: string }>(),
    );
    let generatorsWithCounts = ref(
      new Map<
        string,
        {
          id: string;
          description: string;
          count: number;
          generatorMurn: string;
        }
      >(),
    );

    //TODO: probably not the best approach.
    const handlerNavItem = computed(
      () => navigationItems.find((x) => x.label === "home")!.items.find((x) => x.name === routeNames.handlers)!,
    );

    const getCountyCounts = async () => {
      getLookupsFromLookupGroup("CountyCode").then((r) => {
        r._items.forEach((item) => {
          countyCodesWithCounts.value.set(item.code, {
            id: item.code,
            description: item.description,
            count: 0,
            countyCodeMurn: item.murn,
          });
        });
        countyCodesWithCounts.value.forEach((val) => {
          getHandlers({ countyCodeMurn: val.countyCodeMurn, statusMurn: LE.HandlerStatus.Active, take: 1 }).then(
            (r) => {
              val.count = r.data.totalResultsCount;
            },
          );
        });
      });
    };

    const getGeneratorCounts = () => {
      getLookupsFromLookupGroup("FederalWasteGeneratorType").then((r) => {
        r._items.forEach((item) => {
          if (
            item.metadata?.filtersOn?.FederalWasteGeneratorCategory?.values.includes(
              LE.FederalWasteGeneratorCategory.Filter,
            )
          ) {
            generatorsWithCounts.value.set(item.code, {
              id: item.code,
              description: item.longDescription,
              count: 0,
              generatorMurn: item.murn,
            });
          }
        });
        generatorsWithCounts.value.forEach((val) => {
          getHandlers({
            federalWasteGeneratorMurn: val.generatorMurn,
            statusMurn: LE.HandlerStatus.Active,
            take: 1,
          }).then((r) => {
            {
              val.count = r.data.totalResultsCount;
            }
          });
        });
      });
    };

    const hasReadHandlerAR = new AuthRequest();
    hasReadHandlerAR.permissionName = "ReadHandler";
    authorizationClient.isAuthorized(hasReadHandlerAR).then((hasReadHandler) => {
      if (hasReadHandler) {
        getCountyCounts();
        getGeneratorCounts();
      }
    }, handlePreempts);

    return {
      userRef,
      routeNames,
      countyCodesWithCounts,
      generatorsWithCounts,
      handlerNavItem,
      appSettingsStore,
      BaseButton,
      LE,
    };
  },
  components: {
    BaseButton,
    LogoutButton,
    DashboardDataButton,
    DashboardDataButtonGroup,
  },
});
</script>
